import React from "react";
import { SettingField } from "./shared";
import type { SettingsForm } from "../params/types";

interface SettingsProps {
  settingsForm?: SettingsForm | null;
  setSetting: <K extends keyof SettingsForm>(
    key: K,
    value: SettingsForm[K],
  ) => void;
  onSaveSettings?: React.FormEventHandler<HTMLFormElement>;
  busy?: boolean;
  /** Re-derive every profile's .viewport.json from the limits above. */
  onRecalculateViewports?: () => void;
  recalculating?: boolean;
  /** Clear Threads/day and Messages/thread/day counters for all accounts. */
  onResetDailyLimits?: () => void;
  resettingLimits?: boolean;
}

export default function Settings({
  settingsForm,
  setSetting,
  onSaveSettings,
  busy,
  onRecalculateViewports,
  recalculating,
  onResetDailyLimits,
  resettingLimits,
}: SettingsProps) {
  if (!settingsForm) {
    return (
      <div className="ib-settings-view">
        <div className="ib-empty">Loading settings…</div>
      </div>
    );
  }

  return (
    <div className="ib-settings-view">
      <form
        className="ib-settings-form ib-settings-form--standalone"
        onSubmit={onSaveSettings}
      >
        <section className="ib-settings-section">
          <div className="ib-settings-section__head">
            <h2 className="ib-settings-section__title">Global</h2>
            <p className="ib-settings-section__hint">
              Applies to every Instagram account on this bot.
            </p>
          </div>
          <div className="ib-settings-section__body">
            <div className="ib-settings-checks">
              <label className="ib-settings-check">
                <input
                  type="checkbox"
                  checked={Boolean(settingsForm.hide_suspended_accounts)}
                  onChange={(e) =>
                    setSetting("hide_suspended_accounts", e.target.checked)
                  }
                />
                <span className="ib-settings-check__body">
                  <span>Hide suspended accounts</span>
                  <span className="ib-muted">
                    Suspended accounts drop out of Accounts, Progress,
                    Messages, Followers, Logs, and Export. They stay saved.
                    Save to apply.
                  </span>
                </span>
              </label>
            </div>
            <div className="ib-settings-grid">
              <SettingField
                helpKey="max_concurrent_sessions"
                label="Max concurrent sessions"
              >
                <input
                  type="number"
                  min={1}
                  value={settingsForm.max_concurrent_sessions ?? 10}
                  onChange={(e) =>
                    setSetting("max_concurrent_sessions", e.target.value)
                  }
                />
              </SettingField>

              <SettingField
                helpKey="blocklist_usernames"
                label="Blocklist usernames"
                className="ib-setting-field--wide"
              >
                <textarea
                  className="ib-settings-blocklist"
                  rows={6}
                  spellCheck={false}
                  autoComplete="off"
                  value={settingsForm.blocklist_usernames ?? ""}
                  onChange={(e) =>
                    setSetting("blocklist_usernames", e.target.value)
                  }
                  placeholder={"bot_account\nspammer"}
                />
              </SettingField>
            </div>
          </div>
        </section>

        <section className="ib-settings-section">
          <div className="ib-settings-section__head">
            <h2 className="ib-settings-section__title">Auto rotation</h2>
            <p className="ib-settings-section__hint">
              Start all keeps one group open, then switches to the next group.
              Accounts are taken in the order they were added, oldest first.
              Save, then press Start
              all on Progress.
            </p>
          </div>
          <div className="ib-settings-section__body">
            <div className="ib-settings-checks">
              <label className="ib-settings-check">
                <input
                  type="checkbox"
                  checked={Boolean(settingsForm.rotation_enabled)}
                  onChange={(e) =>
                    setSetting("rotation_enabled", e.target.checked)
                  }
                />
                <span className="ib-settings-check__body">
                  <span>Enable auto rotation</span>
                  <span className="ib-muted">
                    One group works for the shift below, then the next group
                    starts. After the last group, the first one runs again.
                  </span>
                </span>
              </label>
            </div>
            <div className="ib-settings-grid">
              <SettingField
                helpKey="rotation_batch_size"
                label="Accounts per group"
              >
                <input
                  type="number"
                  min={1}
                  max={500}
                  value={settingsForm.rotation_batch_size ?? 5}
                  onChange={(e) =>
                    setSetting("rotation_batch_size", e.target.value)
                  }
                />
              </SettingField>
              <SettingField
                helpKey="rotation_shift_minutes"
                label="Minutes per group"
              >
                <input
                  type="number"
                  min={1}
                  max={1440}
                  value={settingsForm.rotation_shift_minutes ?? 30}
                  onChange={(e) =>
                    setSetting("rotation_shift_minutes", e.target.value)
                  }
                />
              </SettingField>
            </div>
          </div>
        </section>

        <section className="ib-settings-section">
          <div className="ib-settings-section__head">
            <h2 className="ib-settings-section__title">Browser window</h2>
            <p className="ib-settings-section__hint">
              Limits for the per-profile window size the bot rolls on first
              launch. Changing the caps only affects new profiles until you
              recalculate.
            </p>
          </div>
          <div className="ib-settings-section__body">
            <div className="ib-settings-grid">
              <SettingField
                helpKey="browser_max_width"
                label="Max window width (px)"
              >
                <input
                  type="number"
                  min={640}
                  max={3840}
                  value={settingsForm.browser_max_width ?? 1700}
                  onChange={(e) =>
                    setSetting("browser_max_width", e.target.value)
                  }
                />
              </SettingField>

              <SettingField
                helpKey="browser_max_height"
                label="Max window height (px)"
              >
                <input
                  type="number"
                  min={640}
                  max={3840}
                  value={settingsForm.browser_max_height ?? 900}
                  onChange={(e) =>
                    setSetting("browser_max_height", e.target.value)
                  }
                />
              </SettingField>
            </div>
            <div className="ib-settings-recalc">
              <button
                type="button"
                className="jf-btn jf-btn-sm"
                disabled={recalculating || busy}
                onClick={onRecalculateViewports}
              >
                {recalculating
                  ? "Recalculating…"
                  : "Recalculate viewports"}
              </button>
              <span className="ib-settings-recalc__hint">
                Rewrites every account&apos;s stored window size to fit the
                caps above (takes effect on the next browser launch).
              </span>
            </div>
          </div>
        </section>

        <section className="ib-settings-section">
          <div className="ib-settings-section__head">
            <h2 className="ib-settings-section__title">Daily limits</h2>
            <p className="ib-settings-section__hint">
              Threads/day and Messages/thread/day counters (UTC). Reset when you
              need the bot to send again after hitting a cap.
            </p>
          </div>
          <div className="ib-settings-section__body">
            <div className="ib-settings-recalc">
              <button
                type="button"
                className="jf-btn jf-btn-sm"
                disabled={resettingLimits || busy}
                onClick={onResetDailyLimits}
              >
                {resettingLimits ? "Resetting…" : "Reset daily limits"}
              </button>
              <span className="ib-settings-recalc__hint">
                Clears send counters for every account so Threads/day and
                Messages/thread/day start fresh.
              </span>
            </div>
          </div>
        </section>

        <div className="ib-settings-footer">
          <button
            className="jf-btn jf-btn-primary jf-btn-sm"
            type="submit"
            disabled={busy}
          >
            {busy ? "Saving…" : "Save settings"}
          </button>
        </div>
      </form>
    </div>
  );
}
